import { lazy, Suspense, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiGet, apiPost, apiPut } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'

// MapLibre only loads when this page shows the map.
const WalkTracks = lazy(() => import('../components/WalkTracks.tsx').then((m) => ({ default: m.WalkTracks })))

/**
 * Canvassers for the walk app (docs/walk-app.md §2).
 *
 * Everything a manager needs to put a volunteer on a phone and deal with what
 * goes wrong: create the account, read out the campaign PIN, unlock a phone
 * after five wrong PINs, revoke a lost or stolen one. Every action is a stored
 * function that checks the caller itself (migrations 0076–0078); this screen
 * only decides what to offer.
 *
 * Passwords are shown once — stored only as a hash — and handed over in
 * person. The PIN can be looked up again, and each look is audited.
 */

interface Canvasser {
  user_id: string
  username: string
  display_name: string | null
  status: string
  created_at: string
  has_password: boolean
  campaign_count: number
  device_id: string | null
  device_registered_at: string | null
  device_last_seen_at: string | null
  device_locked_at: string | null
  device_lock_reason: string | null
  device_failed_pins: number | null
  phone: string | null
}

interface PinInfo {
  pin: string
  pin_version: number
  set_at: string
}

type Issued = { title: string; lines: [string, string][] }

const REVOKE_REASONS = [
  ['lost_or_stolen', 'Lost or stolen'],
  ['new_phone', 'Switching to a new phone'],
  ['left_campaign', 'Left the campaign'],
  ['other', 'Other'],
] as const

const input = 'rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink'
const label = 'mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase'
const primary =
  'rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50'
const secondary =
  'rounded-md border border-line px-2.5 py-1.5 text-xs font-semibold hover:border-brand-400 disabled:opacity-50'

function when(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function message(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 409) {
    return 'That username is taken. Usernames are unique across all campaigns — try adding a number or initial.'
  }
  if (caught instanceof ApiError && caught.code === 'weak_password') {
    return 'Choose a different password — not their username, one repeated character or only digits.'
  }
  if (caught instanceof ApiError && caught.status === 403) {
    return 'You are not allowed to do that on this campaign.'
  }
  return caught instanceof Error ? caught.message : 'That did not work.'
}

/** The server's rule is 8; saying so here saves a round trip. */
const MIN_PASSWORD = 8

export function Canvassers() {
  const client = useQueryClient()
  const { campaignId, campaign } = useSelectedCampaign()
  const allowed = campaign?.can_manage_canvassers === true

  const [issued, setIssued] = useState<Issued | null>(null)
  const [error, setError] = useState<string | null>(null)

  // A password or PIN shown for one campaign must not stay on screen after
  // switching to another in the header.
  useEffect(() => {
    setIssued(null)
    setError(null)
    setResetting(null)
    setResetDraft('')
    setProfiling(null)
  }, [campaignId])
  const [pin, setPin] = useState<PinInfo | null>(null)
  const [pinShownFor, setPinShownFor] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['canvassers', campaignId],
    queryFn: () => apiGet<{ canvassers: Canvasser[] }>(`/campaigns/${campaignId}/canvassers`, { background: true }),
    enabled: Boolean(campaignId) && allowed,
    // A locked phone should show up without a reload while the manager has the
    // screen open — that is usually why they opened it.
    refetchInterval: 30_000,
  })

  const refresh = () => client.invalidateQueries({ queryKey: ['canvassers', campaignId] })
  const fail = (caught: unknown) => setError(message(caught))
  const ok = () => setError(null)

  // Reading the PIN is audited, so it is fetched when asked for, not on load.
  const showPin = useMutation({
    mutationFn: () =>
      apiGet<{ pin: PinInfo | null }>(`/campaigns/${campaignId}/canvasser-pin`),
    onSuccess: (r) => {
      ok()
      setPin(r.pin)
      setPinShownFor(campaignId)
    },
    onError: fail,
  })

  const changePin = useMutation({
    mutationFn: () =>
      apiPost<{ pin: string; pin_version: number }>(`/campaigns/${campaignId}/canvasser-pin`),
    onSuccess: (r) => {
      ok()
      setPin({ pin: r.pin, pin_version: r.pin_version, set_at: new Date().toISOString() })
      setPinShownFor(campaignId)
    },
    onError: fail,
  })

  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const create = useMutation({
    mutationFn: () =>
      apiPost<{ username: string }>(`/campaigns/${campaignId}/canvassers`, {
        username: username.trim(),
        ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        password,
      }),
    onSuccess: async (r) => {
      ok()
      setIssued({
        title: `New canvasser: ${displayName.trim() || r.username}`,
        lines: [
          ['Username', r.username],
          ['Password', password],
        ],
      })
      setUsername('')
      setDisplayName('')
      setPassword('')
      setPhone('')
      await refresh()
    },
    onError: fail,
  })

  const [attachName, setAttachName] = useState('')
  const attach = useMutation({
    mutationFn: () =>
      apiPost(`/campaigns/${campaignId}/canvassers/attach`, { username: attachName.trim() }),
    onSuccess: async () => {
      ok()
      setAttachName('')
      await refresh()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError && caught.status === 403
          ? 'Could not add that username. You can only add someone who works solely on campaigns you manage — check the spelling, or ask whoever manages their other campaigns.'
          : message(caught),
      ),
  })

  // Name and phone (0106): the walker being edited, and the drafts.
  const [profiling, setProfiling] = useState<Canvasser | null>(null)
  const [nameDraft, setNameDraft] = useState('')
  const [phoneDraft, setPhoneDraft] = useState('')
  const saveProfile = useMutation({
    mutationFn: (c: Canvasser) =>
      apiPut(`/campaigns/${campaignId}/canvassers/${c.user_id}/profile`, {
        display_name: nameDraft.trim() || null,
        phone: phoneDraft.trim() || null,
      }),
    onSuccess: async () => {
      ok()
      setProfiling(null)
      await refresh()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError && caught.status === 403
          ? 'This walker also works on a campaign you do not manage, so their details have to be changed by whoever manages all of their campaigns.'
          : message(caught),
      ),
  })

  // The canvasser whose new password is being typed, and what has been typed.
  const [resetting, setResetting] = useState<Canvasser | null>(null)
  const [resetDraft, setResetDraft] = useState('')
  const newPassword = useMutation({
    mutationFn: ({ c, pw }: { c: Canvasser; pw: string }) =>
      apiPost(`/campaigns/${campaignId}/canvassers/${c.user_id}/password`, { password: pw }).then(() => ({ c, pw })),
    onSuccess: async ({ c, pw }) => {
      ok()
      setIssued({
        title: `New password for ${c.display_name ?? c.username}`,
        lines: [
          ['Username', c.username],
          ['Password', pw],
        ],
      })
      setResetting(null)
      setResetDraft('')
      await refresh()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError && caught.status === 403
          ? 'This canvasser also works on a campaign you do not manage, so a new password has to come from whoever manages all of their campaigns.'
          : message(caught),
      ),
  })

  const unlock = useMutation({
    mutationFn: (c: Canvasser) =>
      apiPost(`/campaigns/${campaignId}/canvasser-devices/${c.device_id}/unlock`),
    onSuccess: async () => {
      ok()
      await refresh()
    },
    onError: fail,
  })

  const [revoking, setRevoking] = useState<Canvasser | null>(null)
  const [revokeReason, setRevokeReason] = useState<string>('lost_or_stolen')
  const revoke = useMutation({
    mutationFn: (c: Canvasser) =>
      apiPost(`/campaigns/${campaignId}/canvasser-devices/${c.device_id}/revoke`, {
        reason: revokeReason,
      }),
    onSuccess: async () => {
      ok()
      setRevoking(null)
      await refresh()
    },
    onError: fail,
  })

  const remove = useMutation({
    mutationFn: (c: Canvasser) =>
      apiPost(`/campaigns/${campaignId}/canvassers/${c.user_id}/remove`),
    onSuccess: async () => {
      ok()
      await refresh()
    },
    onError: fail,
  })

  const canvassers = list.data?.canvassers ?? []
  const shownPin = pinShownFor === campaignId ? pin : null

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight">Walkers</h1>
      <p className="mt-1 text-sm text-ink-muted">
        Accounts for the walk app. Each canvasser signs in on their phone once a day with a
        username, a password and the campaign PIN.
      </p>


      {campaign && !allowed ? (
        <p className="mt-6 text-sm text-ink-muted">
          Only the Campaign Manager can manage canvassers on this campaign.
        </p>
      ) : null}

      {error ? (
        <p
          role="alert"
          className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm text-ink"
        >
          {error}
        </p>
      ) : null}

      {issued ? (
        <section className="mt-4 rounded-lg border border-gold-500 bg-surface-sunken p-4">
          <h2 className="text-sm font-semibold">{issued.title}</h2>
          <p className="mt-1 text-xs text-ink-muted">
            Write this down or copy it now — once you leave this page the password cannot be looked
            up again. Hand it over in person.
          </p>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            {issued.lines.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-xs font-medium tracking-wide text-ink-muted uppercase">{k}</dt>
                <dd>
                  <code className="rounded border border-line bg-surface px-2 py-0.5 text-base font-semibold">
                    {v}
                  </code>
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className={secondary}
              onClick={() =>
                void navigator.clipboard.writeText(issued.lines.map(([k, v]) => `${k}: ${v}`).join('\n'))
              }
            >
              Copy
            </button>
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setIssued(null)}>
              Done
            </button>
          </div>
        </section>
      ) : null}

      {allowed ? (
        <>
          <section className="mt-6 rounded-lg border border-line bg-surface-sunken p-4">
            <h2 className="text-sm font-semibold">Campaign PIN</h2>
            <p className="mt-1 text-xs text-ink-muted">
              Everyone canvassing for this campaign uses the same PIN. Read it out at the start of
              the day. It also picks the campaign, so it is different from every other campaign’s.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {shownPin ? (
                <>
                  <span
                    data-testid="campaign-pin"
                    className="rounded-md border border-line bg-surface px-3 py-1 font-mono text-2xl font-semibold tracking-[0.3em]"
                  >
                    {shownPin.pin}
                  </span>
                  <span className="text-xs text-ink-muted">set {when(shownPin.set_at)}</span>
                  <button type="button" className={secondary} onClick={() => setPin(null)}>
                    Hide
                  </button>
                </>
              ) : pinShownFor === campaignId ? (
                <span className="text-sm">
                  No PIN yet — canvassers cannot sign in until you set one.
                </span>
              ) : (
                <button
                  type="button"
                  className={secondary}
                  disabled={showPin.isPending}
                  onClick={() => showPin.mutate()}
                >
                  Show PIN
                </button>
              )}
              <button
                type="button"
                className={secondary}
                disabled={changePin.isPending}
                onClick={() => {
                  const warning = shownPin
                    ? 'Change the PIN? Everyone signed in on this campaign is signed out at their next check-in and must sign in again with the new PIN.'
                    : 'Set a new campaign PIN? If this campaign already has one, everyone signed in must sign in again with the new PIN.'
                  if (confirm(warning)) changePin.mutate()
                }}
              >
                {shownPin ? 'Change PIN' : 'Set new PIN'}
              </button>
            </div>
          </section>

          <section className="mt-6 rounded-lg border border-line bg-surface-sunken p-4">
            <h2 className="text-sm font-semibold">Add a canvasser</h2>
            <form
              className="mt-3 flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault()
                create.mutate()
              }}
            >
              <label className="w-full text-sm sm:w-auto">
                <span className={label}>Username</span>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. maria.g"
                  autoCapitalize="none"
                  autoCorrect="off"
                  pattern="[A-Za-z0-9][A-Za-z0-9._\-]{2,31}"
                  title="3–32 letters, numbers, dots, dashes or underscores"
                  className={`${input} w-full sm:w-48`}
                />
              </label>
              <label className="w-full text-sm sm:w-auto">
                <span className={label}>Name (optional)</span>
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Maria Garcia"
                  className={`${input} w-full sm:w-56`}
                />
              </label>
              <label className="w-full text-sm sm:w-auto">
                <span className={label}>Phone (optional)</span>
                <input
                  id="cv-new-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  maxLength={32}
                  className={`${input} w-full sm:w-40`}
                />
              </label>
              <label className="w-full text-sm sm:w-auto">
                <span className={label}>Password</span>
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={`at least ${MIN_PASSWORD} characters`}
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck={false}
                  minLength={MIN_PASSWORD}
                  maxLength={128}
                  className={`${input} w-full sm:w-48`}
                />
              </label>
              <button
                type="submit"
                className={primary}
                disabled={username.trim().length < 3 || password.length < MIN_PASSWORD || create.isPending}
              >
                {create.isPending ? 'Creating…' : 'Create account'}
              </button>
            </form>
            <p className="mt-2 text-xs text-ink-muted">
              You choose the password and hand it over in person — something easy to type on a phone.
              The first phone they sign in on becomes their phone.
            </p>

            <details className="mt-4">
              <summary className="cursor-pointer text-xs font-semibold text-ink-muted hover:text-ink">
                Someone who already canvasses for another of your campaigns
              </summary>
              <form
                className="mt-3 flex flex-wrap items-end gap-3"
                onSubmit={(e) => {
                  e.preventDefault()
                  attach.mutate()
                }}
              >
                <label className="w-full text-sm sm:w-auto">
                  <span className={label}>Their username</span>
                  <input
                    value={attachName}
                    onChange={(e) => setAttachName(e.target.value)}
                    autoCapitalize="none"
                    autoCorrect="off"
                    className={`${input} w-full sm:w-48`}
                  />
                </label>
                <button type="submit" className={secondary} disabled={attachName.trim().length < 3 || attach.isPending}>
                  Add to this campaign
                </button>
              </form>
              <p className="mt-2 text-xs text-ink-muted">
                They keep the same username, password and phone, and sign in with this campaign’s
                PIN to work on it.
              </p>
            </details>
          </section>

          <h2 className="mt-8 text-sm font-semibold tracking-wide text-ink-muted uppercase">
            On this campaign
          </h2>
          {list.isPending ? (
            <p className="mt-2 text-sm text-ink-muted">Loading…</p>
          ) : canvassers.length === 0 ? (
            <p className="mt-2 text-sm text-ink-muted">No canvassers yet.</p>
          ) : (
            <ul className="mt-2 divide-y divide-line rounded-lg border border-line">
              {canvassers.map((c) => (
                <li key={c.user_id} className="flex flex-wrap items-start gap-x-6 gap-y-3 p-4" data-testid="canvasser">
                  <div className="min-w-0 flex-1 basis-40">
                    <div className="font-medium">{c.display_name ?? c.username}</div>
                    <div className="text-xs text-ink-muted">
                      {c.username}
                      {c.phone ? ` · ${c.phone}` : ''}
                      {c.campaign_count > 1
                        ? ` · also on ${c.campaign_count - 1} other campaign${c.campaign_count > 2 ? 's' : ''}`
                        : ''}
                    </div>
                  </div>

                  <div className="min-w-0 flex-1 basis-40 text-sm">
                    <PhoneState c={c} />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {c.device_locked_at ? (
                      <button
                        type="button"
                        className={primary}
                        disabled={unlock.isPending}
                        onClick={() => {
                          if (confirm(`Unlock ${c.display_name ?? c.username}’s phone? Only do this once you have checked it is really them.`)) {
                            unlock.mutate(c)
                          }
                        }}
                      >
                        Unlock phone
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className={secondary}
                      onClick={() => {
                        setResetting(resetting?.user_id === c.user_id ? null : c)
                        setResetDraft('')
                      }}
                    >
                      New password
                    </button>
                    <button
                      type="button"
                      className={secondary}
                      onClick={() => {
                        setProfiling(profiling?.user_id === c.user_id ? null : c)
                        setNameDraft(c.display_name ?? '')
                        setPhoneDraft(c.phone ?? '')
                      }}
                    >
                      Name &amp; phone
                    </button>
                    {c.device_id ? (
                      <button
                        type="button"
                        className={secondary}
                        onClick={() => {
                          setRevokeReason('lost_or_stolen')
                          setRevoking(c)
                        }}
                      >
                        Revoke phone…
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="px-2 text-xs text-ink-muted hover:text-ink"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (confirm(`Take ${c.display_name ?? c.username} off this campaign? If they are signed in for it, they are signed out.`)) {
                          remove.mutate(c)
                        }
                      }}
                    >
                      Remove
                    </button>
                  </div>

                  {revoking?.user_id === c.user_id ? (
                    <div className="w-full rounded-md border border-line bg-surface-sunken p-3" role="group" aria-label="Revoke phone">
                      <p className="text-sm">
                        Revoking cuts this phone off now. It deletes the campaign’s data the next
                        time it connects, and <strong>their password stops working</strong> — to
                        put them on another phone, make them a new password afterwards.
                      </p>
                      <div className="mt-3 flex flex-wrap items-end gap-3">
                        <label className="w-full text-sm sm:w-auto">
                          <span className={label}>Why</span>
                          <select value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} className={input}>
                            {REVOKE_REASONS.map(([v, l]) => (
                              <option key={v} value={v}>{l}</option>
                            ))}
                          </select>
                        </label>
                        <button type="button" className={primary} disabled={revoke.isPending} onClick={() => revoke.mutate(c)}>
                          {revoke.isPending ? 'Revoking…' : 'Revoke phone'}
                        </button>
                        <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setRevoking(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {profiling?.user_id === c.user_id ? (
                    <form
                      className="w-full rounded-md border border-line bg-surface-sunken p-3"
                      aria-label="Name and phone"
                      onSubmit={(e) => {
                        e.preventDefault()
                        saveProfile.mutate(c)
                      }}
                    >
                      <div className="flex flex-wrap items-end gap-3">
                        <label className="w-full text-sm sm:w-auto">
                          <span className={label}>Name</span>
                          <input id={`pf-name-${c.user_id}`} value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                            maxLength={200} className={`${input} w-full sm:w-56`} />
                        </label>
                        <label className="w-full text-sm sm:w-auto">
                          <span className={label}>Phone</span>
                          <input id={`pf-phone-${c.user_id}`} type="tel" value={phoneDraft} onChange={(e) => setPhoneDraft(e.target.value)}
                            maxLength={32} className={`${input} w-full sm:w-40`} />
                        </label>
                        <button type="submit" className={primary} disabled={saveProfile.isPending}>Save</button>
                        <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setProfiling(null)}>
                          Cancel
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-ink-muted">For you to reach them. It is never shown in the app.</p>
                    </form>
                  ) : null}

                  {resetting?.user_id === c.user_id ? (
                    <form
                      className="w-full rounded-md border border-line bg-surface-sunken p-3"
                      aria-label="New password"
                      onSubmit={(e) => {
                        e.preventDefault()
                        newPassword.mutate({ c, pw: resetDraft })
                      }}
                    >
                      <p className="text-sm">The old password stops working as soon as you save this one.</p>
                      <div className="mt-3 flex flex-wrap items-end gap-3">
                        <label className="w-full text-sm sm:w-auto">
                          <span className={label}>New password</span>
                          <input
                            value={resetDraft}
                            onChange={(e) => setResetDraft(e.target.value)}
                            placeholder={`at least ${MIN_PASSWORD} characters`}
                            autoCapitalize="none"
                            autoCorrect="off"
                            autoComplete="off"
                            spellCheck={false}
                            minLength={MIN_PASSWORD}
                            maxLength={128}
                            autoFocus
                            className={`${input} w-full sm:w-56`}
                          />
                        </label>
                        <button
                          type="submit"
                          className={primary}
                          disabled={resetDraft.length < MIN_PASSWORD || newPassword.isPending}
                        >
                          {newPassword.isPending ? 'Saving…' : 'Save password'}
                        </button>
                        <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setResetting(null)}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <HeldUploads campaignId={campaignId} />
          <Suspense fallback={null}>
            <WalkTracks campaignId={campaignId} />
          </Suspense>
        </>
      ) : null}
    </div>
  )
}

interface Held {
  held_id: string
  received_at: string
  username: string | null
  display_name: string | null
  reason: 'device_revoked' | 'not_assigned'
  item: { kind: string; disposition?: string; outcome?: string; occurred_at?: string }
  person_name: string | null
}

const HELD_REASON: Record<Held['reason'], string> = {
  device_revoked: 'from a phone you revoked',
  not_assigned: 'for someone not on their walk',
}

/**
 * Finished doors held for review (0090): uploaded by a revoked phone, or naming
 * someone the canvasser was never given. Nothing here counts until accepted.
 * Only shown when there is something to decide.
 */
function HeldUploads({ campaignId }: { campaignId: string }) {
  const client = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const held = useQuery({
    queryKey: ['held-uploads', campaignId],
    queryFn: () => apiGet<{ held: Held[] }>(`/campaigns/${campaignId}/held-uploads`),
    enabled: Boolean(campaignId),
  })
  const review = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      apiPost(`/campaigns/${campaignId}/held-uploads/${id}`, { accept }),
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['held-uploads', campaignId] })
    },
    onError: (e) => setError(message(e)),
  })

  const rows = held.data?.held ?? []
  if (rows.length === 0) return null

  return (
    <section className="mt-10 rounded-lg border border-gold-500 p-4">
      <h2 className="text-sm font-semibold">Held for review ({rows.length})</h2>
      <p className="mt-1 max-w-prose text-sm text-ink-muted">
        Doors a canvasser finished that are not counted yet. Accept to count them as that
        canvasser's work; discard to leave them out. Either way it is recorded.
      </p>
      {error ? (
        <p role="alert" className="mt-3 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}
      <ul className="mt-3 divide-y divide-line">
        {rows.map((h) => (
          <li key={h.held_id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
            <div className="min-w-0">
              <div>
                <span className="font-medium">{h.display_name ?? h.username}</span>{' '}
                {h.item.kind === 'visit'
                  ? `marked a door ${h.item.outcome === 'inaccessible' ? 'could not access' : 'not home'}`
                  : `recorded ${h.item.disposition ?? 'an answer'}${h.person_name ? ` for ${h.person_name}` : ''}`}
              </div>
              <div className="text-xs text-ink-muted">
                {HELD_REASON[h.reason]} · {when(h.item.occurred_at ?? h.received_at)}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => review.mutate({ id: h.held_id, accept: true })}
                className="text-xs font-semibold text-brand-700 hover:underline"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => review.mutate({ id: h.held_id, accept: false })}
                className="text-xs text-ink-muted hover:text-ink"
              >
                Discard
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** What the manager needs to know about the phone, in words. */
function PhoneState({ c }: { c: Canvasser }) {
  if (c.device_locked_at) {
    return (
      <div>
        <div className="font-semibold">Phone locked</div>
        <div className="text-xs text-ink-muted">
          {c.device_lock_reason === 'pin_attempts' ? 'Five wrong PINs' : 'Locked'} ·{' '}
          {when(c.device_locked_at)}. Check it is them, then unlock.
        </div>
      </div>
    )
  }
  if (!c.has_password) {
    return (
      <div>
        <div className="font-semibold">Needs a new password</div>
        <div className="text-xs text-ink-muted">Their phone was revoked. Make a new password to sign in again.</div>
      </div>
    )
  }
  if (!c.device_id) {
    return (
      <div>
        <div>No phone yet</div>
        <div className="text-xs text-ink-muted">Their first sign-in sets up their phone.</div>
      </div>
    )
  }
  return (
    <div>
      <div>Phone set up</div>
      <div className="text-xs text-ink-muted">
        Last seen {when(c.device_last_seen_at)}
        {c.device_failed_pins ? ` · ${c.device_failed_pins} wrong PIN${c.device_failed_pins > 1 ? 's' : ''}` : ''}
      </div>
    </div>
  )
}
